import React from 'react';
import { Panel } from '../../compat/PanelCompat';
import PropTypes from 'prop-types';

import DataComparisonTable from './comparisonTables/DataComparisonTable';
import DataComparisonTableMobile from './comparisonTables/DataComparisonTableMobile';
import IconComparisonTable from './comparisonTables/IconComparisonTable';
import IconComparisonTableMobile from './comparisonTables/IconComparisonTableMobile';

import addonsIcon from '../../../../image/Icons/Table-Todo.svg';
import modulesIcon from '../../../../image/Icons/Table-Appstore.svg';
import mobilityIcon from '../../../../image/Icons/Table-Pcheck.svg';
import propertyDataIcon from '../../../../image/Icons/Table-House-Pricing.svg';
import reportsIcon from '../../../../image/Icons/Table-Piechart.svg';
import toolIcon from '../../../../image/Icons/Benchmarking-active.svg';
import usersIcon from '../../../../image/Icons/Table-Users.svg';

import './ComparisonTablePanel.css';

/**
 * ComparisonTablePanel.
 * Comparison table panel for products.
 *
 * @author  Manny
 * @param   {Boolean}     props.buyNow          Check if method is buyNow.
 * @param   {Array}       props.data            Product data.
 * @param   {String}      props.dataType        Product data type.
 * @param   {Boolean}     props.defaultExpanded Determines if panel is expanded by default.
 * @param   {String}      props.iconType        Icon to display.
 * @param   {Number}      props.index           Product data index.
 * @param   {String}      props.industryKey     Name of the industry.
 * @param   {Object}      props.industryPackage List of packages for the industry.
 * @param   {Boolean}     props.isOpen          If the panel is expanded or not.
 * @param   {Boolean}     props.isMobile        Determines if tables are for mobile format.
 * @param   {Function}    props.onClick         Handles onClick for the panel.
 * @param   {String}      props.packageKey      Name of the package.
 * @param   {String}      props.tableType       Table type to render.
 * @param   {String}      props.title           Title for the data.
 * @returns {JSX.Element} The component markup.
 */

const ComparisonTablePanel = ({
  buyNow,
  data,
  dataType,
  defaultExpanded = false,
  iconType,
  index,
  industryKey,
  industryPackage,
  isOpen,
  isMobile = false,
  onClick,
  packageKey,
  tableType,
  title
}) => {
  let panelIcon = null;

  switch (iconType) {
    case 'addonsIcon':
      panelIcon = addonsIcon;
      break;
    case 'modulesIcon':
      panelIcon = modulesIcon;
      break;
    case 'mobilityIcon':
      panelIcon = mobilityIcon;
      break;
    case 'propertyDataIcon':
      panelIcon = propertyDataIcon;
      break;
    case 'reportsIcon':
      panelIcon = reportsIcon;
      break;
    case 'toolIcon':
      panelIcon = toolIcon;
      break;
    case 'usersIcon':
      panelIcon = usersIcon;
      break;
    default:
      break;
  }

  return (
    <Panel defaultExpanded={defaultExpanded}>
      <Panel.Heading className='off-white-bg'>
        <Panel.Title toggle onClick={() => onClick(index)}>
          <div className='tab-header body-semibold'>
            <img className='panel-header-icon' src={panelIcon} alt='' />
            {title}
            <span
              className={
                isOpen
                  ? 'panel-dropdown-icon carat-up'
                  : 'panel-dropdown-icon carat-down'
              }
              aria-hidden='true'
            ></span>
          </div>
        </Panel.Title>
      </Panel.Heading>
      <Panel.Collapse>
        <Panel.Body>
          {tableType === 'data' &&
            (isMobile ? (
              <DataComparisonTableMobile
                industryKey={industryKey}
                industryPackage={industryPackage}
                data={data}
                type={dataType}
                packageKey={packageKey}
              />
            ) : (
              <DataComparisonTable
                industryKey={industryKey}
                industryPackage={industryPackage}
                data={data}
                type={dataType}
                packageKey={packageKey}
                buyNow={buyNow}
              />
            ))}
          {tableType === 'icon' &&
            (isMobile ? (
              <IconComparisonTableMobile
                industryKey={industryKey}
                industryPackage={industryPackage}
                data={data}
                type={dataType}
                packageKey={packageKey}
              />
            ) : (
              <IconComparisonTable
                industryKey={industryKey}
                industryPackage={industryPackage}
                data={data}
                type={dataType}
                packageKey={packageKey}
                buyNow={buyNow}
              />
            ))}
        </Panel.Body>
      </Panel.Collapse>
    </Panel>
  );
};

ComparisonTablePanel.propTypes = {
  buyNow: PropTypes.bool,
  data: PropTypes.array.isRequired,
  dataType: PropTypes.string.isRequired,
  defaultExpanded: PropTypes.bool,
  iconType: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  industryKey: PropTypes.string,
  industryPackage: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  packageKey: PropTypes.string,
  tableType: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired
};

export default ComparisonTablePanel;
